/* XMS Pipe Builder — rev 2026-08-06
   Independent flange selection per end. Checkout is now server-authoritative.

   MODEL
     Pipes   : label | tube OD in | bore in | price per inch $
     Flanges : label | family | weld tube OD in | flange OD in | price $

   The customer picks a tube, then a flange for end A and end B independently.
   Any flange may go on any end. When a flange's native weld OD differs from
   the chosen tube OD the assembly needs an adapter, and the drawing renders a
   tapered transition cone rather than pretending the diameters match. The
   machining cost is already carried in the flange price.

   Rows may be separated by newlines OR semicolons.

   weldments = one per flanged end. WELDS ARE DELIBERATELY NOT DRAWN — these
   joints are welded on the inside. Weldments stay in the price breakdown.

   PRICING AND CHECKOUT — CHANGED IN THIS REVISION
     Every earlier version computed a price in this file and trusted the
     browser to report it: the button posted a bracket-variant id straight
     to /cart/add.js, rounded UP to the nearest $50 step, and nothing
     server-side ever re-checked that the variant matched the configuration.
     A forged request could buy the cheapest bracket while claiming any spec.

     The price shown here is now DISPLAY ONLY. Clicking the button sends the
     CONFIGURATION — tube, flange A, flange B, length — to a Cloudflare
     Worker (CFG.workerUrl), which holds its own independent copy of the
     price tables, recomputes the total from scratch, and creates a Shopify
     Draft Order at that exact price. The browser's number is never trusted
     for the actual charge. This also means the customer now pays the exact
     computed total — no more $50 bracket rounding.

     If this file's tables and the worker's tables ever drift apart, the
     displayed estimate and the final invoice will disagree. Keep them in
     sync by hand until they're pulled from one shared source.
*/

(function () {
  'use strict';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function num(v, d) { var n = parseFloat(v); return isFinite(n) ? n : d; }

  function money(cents) {
    var v = (Math.round(num(cents, 0)) / 100).toFixed(2);
    var parts = v.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + whole + '.' + parts[1];
  }

  function nn(n) { return String(Math.round(num(n, 0) * 100) / 100); }

  /* Flange families look very different in the metal, so the drawing branches
     on family rather than drawing one generic disc:
       cf   ConFlat  — thick, bolt holes, knife-edge ticks on the sealing face
       kf   KF/QF    — thin, NO bolt holes, chamfered outer edge for the clamp
       isok ISO-K    — claw clamps outside the rim, no through holes
       isof ISO-F    — through-bolted, larger rim
       asa  ASA/ANSI — through-bolted with a raised face
       jis  JIS      — as ASA
       vco  VCO      — gland and nut, O-ring groove
       vcr  VCR      — gland and nut, metal-gasket groove
     Unknown families fall back to a plain bolted disc. */
  function flangeStyle(family, label) {
    var f = String(family || '').toLowerCase();
    var l = String(label || '').toLowerCase();
    if (f.indexOf('vcr') >= 0 || f.indexOf('vco') >= 0) {
      return l.indexOf('vco') >= 0 ? 'vco' : 'vcr';
    }
    if (f.indexOf('iso-k') >= 0 || f.indexOf('isok') >= 0) return 'isok';
    if (f.indexOf('iso-f') >= 0 || f.indexOf('isof') >= 0) return 'isof';
    if (f.indexOf('conflat') >= 0 || f.indexOf('cf') >= 0) return 'cf';
    if (f.indexOf('kf') >= 0 || f.indexOf('qf') >= 0) return 'kf';
    if (f.indexOf('asa') >= 0 || f.indexOf('ansi') >= 0) return 'asa';
    if (f.indexOf('jis') >= 0) return 'jis';
    return 'plain';
  }

  function rows(text) {
    var out = [];
    var raw = String(text || '').split(/[;\n\r]+/);
    for (var i = 0; i < raw.length; i++) {
      var t = raw[i].trim();
      if (t && t.charAt(0) !== '#') out.push(t);
    }
    return out;
  }

  /* label | tube OD | bore | price per inch $ */
  function parsePipes(text, fallbackRateCents) {
    var out = [];
    var r = rows(text);
    for (var i = 0; i < r.length; i++) {
      var f = r[i].split('|');
      if (f.length < 3) continue;
      var label = f[0].trim();
      var od = num(f[1], 0);
      var bore = num(f[2], 0);
      var rate = f.length > 3 ? num(f[3], 0) : 0;
      if (!label || !(od > 0)) continue;
      if (!(bore > 0) || bore >= od) bore = od * 0.9;
      out.push({
        label: label, od: od, bore: bore,
        rateCents: rate > 0 ? Math.round(rate * 100) : Math.round(num(fallbackRateCents, 0))
      });
    }
    return out;
  }

  /* label | family | weld tube OD | flange OD | price $ */
  function parseFlanges(text) {
    var out = [];
    var r = rows(text);
    for (var i = 0; i < r.length; i++) {
      var f = r[i].split('|');
      if (f.length < 5) continue;
      var label = f[0].trim();
      var family = f[1].trim();
      var weldOd = num(f[2], 0);
      var od = num(f[3], 0);
      var pr = num(f[4], 0);
      if (!label || !(weldOd > 0) || !(od > 0)) continue;
      if (od <= weldOd) od = weldOd * 1.3;
      out.push({ label: label, family: family, weldOd: weldOd, od: od, cents: Math.round(pr * 100) });
    }
    return out;
  }

  function init(root, sheet, fail) {
    var cfgNode = root.querySelector('[data-pb-config]');
    if (!cfgNode) { fail('Builder configuration block is missing.'); return; }

    var CFG;
    try {
      CFG = JSON.parse(cfgNode.textContent);
    } catch (e) {
      fail('Builder configuration could not be read.');
      return;
    }

    function q(name) { return root.querySelector('[data-pb="' + name + '"]'); }
    var el = {
      sheet: sheet, spec: q('spec'), pipe: q('pipe'), len: q('len'), lenhint: q('lenhint'),
      fa: q('fa'), fb: q('fb'), weldcount: q('weldcount'), lines: q('lines'),
      total: q('total'), charge: q('charge'), cta: q('cta'), status: q('status')
    };
    if (!el.pipe || !el.len || !el.fa || !el.fb) { fail('Builder controls are missing from the page.'); return; }

    var PIPES = parsePipes(CFG.pipes, CFG.pipeRateCents);
    var FLANGES = parseFlanges(CFG.flanges);

    if (!PIPES.length) { fail('No tube sizes configured. Add rows to the Pipes setting.'); return; }
    if (!FLANGES.length) { fail('No flanges configured. Add rows to the Flanges setting.'); return; }

    /* Radial scale. Recomputed per drawing from the parts actually in view
       (see draw), because the catalogue spans roughly 0.4 in to 32 in of
       flange OD. Parts stay in true proportion to one another within any one
       drawing; they are not comparable between drawings. */
    var RSCALE = 26;

    el.pipe.innerHTML = '';
    for (var i = 0; i < PIPES.length; i++) {
      var po = document.createElement('option');
      po.value = String(i);
      po.textContent = PIPES[i].label + ' \u2014 ' + nn(PIPES[i].od) + ' in OD';
      el.pipe.appendChild(po);
    }

    function fillFlanges(sel) {
      sel.innerHTML = '';
      var none = document.createElement('option');
      none.value = '-1';
      none.textContent = 'None \u2014 open end';
      sel.appendChild(none);
      for (var k = 0; k < FLANGES.length; k++) {
        var o = document.createElement('option');
        o.value = String(k);
        o.textContent = FLANGES[k].label + (FLANGES[k].family ? ' (' + FLANGES[k].family + ')' : '');
        sel.appendChild(o);
      }
    }
    fillFlanges(el.fa);
    fillFlanges(el.fb);

    var defP = Math.round(num(CFG.defaultPipe, 0));
    el.pipe.value = String(Math.min(PIPES.length - 1, Math.max(0, defP)));

    var defA = Math.round(num(CFG.defaultFlangeA, 0));
    var defB = Math.round(num(CFG.defaultFlangeB, 0));
    el.fa.value = String(Math.min(FLANGES.length - 1, Math.max(-1, defA)));
    el.fb.value = String(Math.min(FLANGES.length - 1, Math.max(-1, defB)));

    var MIN_L = num(CFG.minLength, 1);
    var MAX_L = num(CFG.maxLength, 72);
    if (el.lenhint) el.lenhint.textContent = MIN_L + ' to ' + MAX_L + ' in, in 0.25 in steps.';

    var S = { pi: 0, len: num(el.len.value, 12), fa: null, fb: null };

    function pick(sel) {
      var v = parseInt(sel.value, 10);
      if (!isFinite(v) || v < 0 || v >= FLANGES.length) return null;
      return FLANGES[v];
    }

    function read() {
      S.pi = Math.max(0, Math.min(PIPES.length - 1, parseInt(el.pipe.value, 10) || 0));
      S.len = num(el.len.value, 0);
      S.fa = pick(el.fa);
      S.fb = pick(el.fb);
    }

    /* Display-only estimate. The worker recomputes this independently at
       checkout time and its number is the one actually charged. */
    function price() {
      var t = PIPES[S.pi];
      var flanges = (S.fa ? 1 : 0) + (S.fb ? 1 : 0);
      var lenValid = S.len >= MIN_L && S.len <= MAX_L;
      var weldCents = flanges * Math.round(num(CFG.weldmentCents, 0));
      var pipeCents = lenValid ? Math.round(S.len * num(t.rateCents, 0)) : 0;
      var flangeCents = (S.fa ? S.fa.cents : 0) + (S.fb ? S.fb.cents : 0);
      return {
        t: t, len: S.len, fa: S.fa, fb: S.fb, flanges: flanges, weldments: flanges,
        lenValid: lenValid, weldCents: weldCents, pipeCents: pipeCents,
        flangeCents: flangeCents, total: weldCents + pipeCents + flangeCents
      };
    }

    /* Per-end geometry. A cone appears whenever the flange's native weld OD
       differs from the chosen tube OD. That is a real adapter, not a fudge. */
    function endGeom(fl, tubeHalf) {
      if (!fl) return { has: false, ft: 0, cone: 0, weldHalf: tubeHalf, flangeHalf: tubeHalf };
      var weldHalf = Math.max(5, (fl.weldOd / 2) * RSCALE);
      var flangeHalf = Math.max((fl.od / 2) * RSCALE, weldHalf + 6);
      var stl = flangeStyle(fl.family, fl.label);
      var ft = Math.max(10, Math.min(20, flangeHalf * 0.3));
      if (stl === 'vcr' || stl === 'vco') ft = ft * 3.0;
      var cone = Math.abs(weldHalf - tubeHalf) > 1.5 ? 34 : 0;
      return { has: true, fl: fl, ft: ft, cone: cone, weldHalf: weldHalf, flangeHalf: flangeHalf, style: stl };
    }

    function draw(p) {
      var VW = 660, VH = 290, cy = 122, PAD = 34;

      var maxOd = p.t.od;
      if (p.fa && p.fa.od > maxOd) maxOd = p.fa.od;
      if (p.fb && p.fb.od > maxOd) maxOd = p.fb.od;
      RSCALE = 52 / (maxOd / 2);

      var tubeHalf = Math.max(6, (p.t.od / 2) * RSCALE);
      var A = endGeom(p.fa, tubeHalf);
      var B = endGeom(p.fb, tubeHalf);

      var ends = A.ft + A.cone + B.ft + B.cone;
      var avail = VW - PAD * 2 - ends;
      var drawLen = p.lenValid ? p.len : 0;
      var pxPerIn = drawLen > 0 ? Math.min(11, avail / drawLen) : 11;
      var bodyW = drawLen > 0 ? Math.max(56, Math.min(avail, drawLen * pxPerIn)) : 96;

      var totalW = bodyW + ends;
      var x0 = (VW - totalW) / 2 + A.ft + A.cone;
      var x1 = x0 + bodyW;
      var maxHalf = Math.max(tubeHalf, A.flangeHalf, B.flangeHalf);
      var s = [];

      var altA = p.fa ? p.fa.label : 'open';
      var altB = p.fb ? p.fb.label : 'open';
      var alt = p.t.label + ' tube, ' + (p.lenValid ? nn(p.len) + ' inches long' : 'length not set') + ', end A ' + altA + ', end B ' + altB + ', ' + p.weldments + ' weldment' + (p.weldments === 1 ? '' : 's');
      s.push('<svg viewBox="0 0 ' + VW + ' ' + VH + '" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="' + esc(alt) + '">');

      s.push('<path class="xms-pb__cl" d="M' + (x0 - A.ft - A.cone - 22).toFixed(1) + ' ' + cy + 'H' + (x1 + B.ft + B.cone + 22).toFixed(1) + '"/>');

      s.push('<rect class="xms-pb__pipe" x="' + x0.toFixed(1) + '" y="' + (cy - tubeHalf).toFixed(1) + '" width="' + bodyW.toFixed(1) + '" height="' + (tubeHalf * 2).toFixed(1) + '"/>');
      s.push('<path class="xms-pb__borel" d="M' + x0.toFixed(1) + ' ' + (cy - tubeHalf * 0.42).toFixed(1) + 'H' + x1.toFixed(1) + 'M' + x0.toFixed(1) + ' ' + (cy + tubeHalf * 0.42).toFixed(1) + 'H' + x1.toFixed(1) + '"/>');

      function cone(xTube, xOuter, g) {
        s.push('<path class="xms-pb__pipe" d="M' + xOuter.toFixed(1) + ' ' + (cy - g.weldHalf).toFixed(1) + 'L' + xTube.toFixed(1) + ' ' + (cy - tubeHalf).toFixed(1) + 'L' + xTube.toFixed(1) + ' ' + (cy + tubeHalf).toFixed(1) + 'L' + xOuter.toFixed(1) + ' ' + (cy + g.weldHalf).toFixed(1) + 'Z"/>');
        s.push('<text class="xms-pb__odtext" x="' + ((xTube + xOuter) / 2).toFixed(1) + '" y="' + (cy + maxHalf + 36).toFixed(1) + '">ADAPTER</text>');
      }

      function flange(fx, g, side) {
        var h = g.flangeHalf, ft = g.ft, st = g.style;
        var x2 = fx + ft;
        var cxm = fx + ft / 2;
        var outX = side < 0 ? fx : x2;
        var inward = side < 0 ? 1 : -1;

        if (st === 'kf') {
          var ch = Math.min(h * 0.32, ft * 0.85);
          s.push('<path class="xms-pb__flange" d="M' + fx.toFixed(1) + ' ' + (cy - h).toFixed(1) + 'L' + x2.toFixed(1) + ' ' + (cy - h + ch).toFixed(1) + 'L' + x2.toFixed(1) + ' ' + (cy + h - ch).toFixed(1) + 'L' + fx.toFixed(1) + ' ' + (cy + h).toFixed(1) + 'Z"/>');
        } else if (st === 'vcr' || st === 'vco') {
          var inX = side < 0 ? x2 : fx;
          var dir = side < 0 ? -1 : 1;
          var at = function (t) { return inX + dir * t * ft; };
          var band = function (t0, t1, half, cls) {
            var a = at(t0), b = at(t1);
            s.push('<rect class="' + cls + '" x="' + Math.min(a, b).toFixed(1) + '" y="' + (cy - half).toFixed(1) +
              '" width="' + Math.abs(b - a).toFixed(1) + '" height="' + (half * 2).toFixed(1) + '"/>');
          };

          var shh = Math.max(g.weldHalf, 4);
          var gbh = Math.max(shh * 1.35, shh + 3);
          var bh = Math.min(h * 0.8, gbh + Math.max(3, h * 0.16));

          band(0, 0.36, shh, 'xms-pb__pipe');
          band(0.34, 0.5, gbh, 'xms-pb__pipe');
          band(0.46, 0.56, bh, 'xms-pb__flange');

          var na = at(0.56), nb = at(1);
          var nx0 = Math.min(na, nb), nx1 = Math.max(na, nb);
          var hc = h * 0.3, sl = (nx1 - nx0) * 0.18;
          s.push('<path class="xms-pb__flange" d="M' + nx0.toFixed(1) + ' ' + (cy - h + hc).toFixed(1) +
            'L' + (nx0 + sl).toFixed(1) + ' ' + (cy - h).toFixed(1) +
            'L' + (nx1 - sl).toFixed(1) + ' ' + (cy - h).toFixed(1) +
            'L' + nx1.toFixed(1) + ' ' + (cy - h + hc).toFixed(1) +
            'L' + nx1.toFixed(1) + ' ' + (cy + h - hc).toFixed(1) +
            'L' + (nx1 - sl).toFixed(1) + ' ' + (cy + h).toFixed(1) +
            'L' + (nx0 + sl).toFixed(1) + ' ' + (cy + h).toFixed(1) +
            'L' + nx0.toFixed(1) + ' ' + (cy + h - hc).toFixed(1) + 'Z"/>');

          var td = [];
          for (var tn = 1; tn < 5; tn++) {
            var tx = nx0 + (nx1 - nx0) * (tn / 5);
            td.push('M' + tx.toFixed(1) + ' ' + (cy - h + hc).toFixed(1) + 'V' + (cy - h * 0.45).toFixed(1));
            td.push('M' + tx.toFixed(1) + ' ' + (cy + h * 0.45).toFixed(1) + 'V' + (cy + h - hc).toFixed(1));
          }
          s.push('<path class="xms-pb__borel" d="' + td.join('') + '"/>');

          var gfx = at(0.56), gd = -dir * Math.max(3, ft * 0.06);
          if (st === 'vco') {
            s.push('<path class="xms-pb__face" d="M' + gfx.toFixed(1) + ' ' + (cy - gbh * 0.55).toFixed(1) +
              'q' + gd.toFixed(1) + ' ' + (gbh * 0.55).toFixed(1) + ' 0 ' + (gbh * 1.1).toFixed(1) + '"/>');
          } else {
            s.push('<path class="xms-pb__face" d="M' + gfx.toFixed(1) + ' ' + (cy - gbh * 0.6).toFixed(1) +
              'h' + gd.toFixed(1) + 'V' + (cy + gbh * 0.6).toFixed(1) + 'h' + (-gd).toFixed(1) + '"/>');
          }
        } else {
          s.push('<rect class="xms-pb__flange" x="' + fx.toFixed(1) + '" y="' + (cy - h).toFixed(1) + '" width="' + ft.toFixed(1) + '" height="' + (h * 2).toFixed(1) + '"/>');
        }

        if (st === 'cf' || st === 'isof' || st === 'asa' || st === 'jis' || st === 'plain') {
          var r = Math.max(1.5, Math.min(3, ft * 0.16));
          var ring = [h - r - 3];
          if (st === 'cf') ring.push(h * 0.62);
          for (var k = 0; k < ring.length; k++) {
            if (ring[k] < g.weldHalf + 3) continue;
            s.push('<circle class="xms-pb__bolt" cx="' + cxm.toFixed(1) + '" cy="' + (cy - ring[k]).toFixed(1) + '" r="' + r.toFixed(1) + '"/>');
            s.push('<circle class="xms-pb__bolt" cx="' + cxm.toFixed(1) + '" cy="' + (cy + ring[k]).toFixed(1) + '" r="' + r.toFixed(1) + '"/>');
          }
        }

        if (st === 'isok') {
          var cw = Math.max(6, ft * 0.7), cl = Math.max(5, h * 0.13);
          var hookX = outX - inward * cw;
          var hx0 = Math.min(hookX, outX), hx1 = Math.max(hookX, outX);
          s.push('<path class="xms-pb__flange" d="M' + hx0.toFixed(1) + ' ' + (cy - h - cl).toFixed(1) +
            'H' + hx1.toFixed(1) + 'V' + (cy - h * 0.55).toFixed(1) +
            'h' + (-inward * cw * 0.45).toFixed(1) + 'V' + (cy - h).toFixed(1) +
            'H' + hx0.toFixed(1) + 'Z"/>');
          s.push('<path class="xms-pb__flange" d="M' + hx0.toFixed(1) + ' ' + (cy + h + cl).toFixed(1) +
            'H' + hx1.toFixed(1) + 'V' + (cy + h * 0.55).toFixed(1) +
            'h' + (-inward * cw * 0.45).toFixed(1) + 'V' + (cy + h).toFixed(1) +
            'H' + hx0.toFixed(1) + 'Z"/>');
        }

        if (st === 'cf') {
          var ke = Math.max(g.weldHalf + 2, h * 0.5);
          var kd = inward * Math.max(3, ft * 0.32);
          s.push('<path class="xms-pb__face" d="M' + outX.toFixed(1) + ' ' + (cy - ke - 3).toFixed(1) +
            'l' + kd.toFixed(1) + ' 3l' + (-kd).toFixed(1) + ' 3M' + outX.toFixed(1) + ' ' + (cy + ke - 3).toFixed(1) +
            'l' + kd.toFixed(1) + ' 3l' + (-kd).toFixed(1) + ' 3"/>');
        }

        if (st === 'asa' || st === 'jis') {
          var rf = Math.max(g.weldHalf + 3, h * 0.5);
          var rw = Math.max(2.5, ft * 0.28);
          var ra = side < 0 ? outX - rw : outX;
          s.push('<rect class="xms-pb__flange" x="' + ra.toFixed(1) + '" y="' + (cy - rf).toFixed(1) +
            '" width="' + rw.toFixed(1) + '" height="' + (rf * 2).toFixed(1) + '"/>');
        }

        if (st === 'kf') {
          var kg = Math.max(g.weldHalf + 2, h * 0.6);
          var kw = inward * Math.max(3, ft * 0.35);
          s.push('<path class="xms-pb__face" d="M' + outX.toFixed(1) + ' ' + (cy - kg).toFixed(1) + 'l' + kw.toFixed(1) + ' 0M' +
            outX.toFixed(1) + ' ' + (cy + kg).toFixed(1) + 'l' + kw.toFixed(1) + ' 0"/>');

          var cjx = outX - inward * Math.max(4, ft * 0.4);
          var cth = Math.max(3, h * 0.09);
          s.push('<path class="xms-pb__flange" d="M' + cjx.toFixed(1) + ' ' + (cy - h - cth * 2).toFixed(1) +
            'H' + outX.toFixed(1) + 'v' + cth.toFixed(1) +
            'H' + (cjx + inward * cth).toFixed(1) + 'V' + (cy + h + cth).toFixed(1) +
            'H' + outX.toFixed(1) + 'v' + cth.toFixed(1) +
            'H' + cjx.toFixed(1) + 'Z"/>');
        }
      }

      function openEnd(jx, dir) {
        s.push('<path class="xms-pb__open" d="M' + jx.toFixed(1) + ' ' + (cy - tubeHalf - 6).toFixed(1) + 'V' + (cy + tubeHalf + 6).toFixed(1) + '"/>');
        s.push('<text class="xms-pb__opentext" x="' + (jx + dir * 8).toFixed(1) + '" y="' + (cy - tubeHalf - 14).toFixed(1) + '" text-anchor="' + (dir < 0 ? 'end' : 'start') + '">OPEN</text>');
      }

      function label(cx, text) {
        var lx = Math.max(72, Math.min(VW - 72, cx));
        s.push('<text class="xms-pb__odtext" x="' + lx.toFixed(1) + '" y="' + (cy - maxHalf - 10).toFixed(1) + '">' + esc(text) + '</text>');
      }

      if (A.has) {
        var axFlange = x0 - A.cone - A.ft;
        if (A.cone) cone(x0, x0 - A.cone, A);
        flange(axFlange, A, -1);
        label(axFlange + A.ft / 2, A.fl.label + ' \u00b7 ' + nn(A.fl.od) + ' OD');
      } else {
        openEnd(x0, -1);
      }

      if (B.has) {
        var bxFlange = x1 + B.cone;
        if (B.cone) cone(x1, x1 + B.cone, B);
        flange(bxFlange, B, 1);
        label(bxFlange + B.ft / 2, B.fl.label + ' \u00b7 ' + nn(B.fl.od) + ' OD');
      } else {
        openEnd(x1, 1);
      }

      if (p.lenValid) {
        var dy = cy + maxHalf + 18;
        s.push('<path class="xms-pb__wit" d="M' + x0.toFixed(1) + ' ' + (cy + tubeHalf + 4).toFixed(1) + 'V' + (dy + 6).toFixed(1) + 'M' + x1.toFixed(1) + ' ' + (cy + tubeHalf + 4).toFixed(1) + 'V' + (dy + 6).toFixed(1) + '"/>');
        s.push('<path class="xms-pb__dim" d="M' + x0.toFixed(1) + ' ' + dy.toFixed(1) + 'H' + x1.toFixed(1) + '"/>');
        s.push('<text class="xms-pb__dimtext" x="' + ((x0 + x1) / 2).toFixed(1) + '" y="' + (dy - 6).toFixed(1) + '">' + esc(nn(p.len) + ' IN') + '</text>');
      }

      var title = p.t.label + '  \u00b7  ' + nn(p.t.od) + ' IN OD TUBE';
      s.push('<text class="xms-pb__tag" x="' + (VW / 2).toFixed(1) + '" y="20">' + esc(title) + '</text>');
      s.push('<text class="xms-pb__scaletext" x="10" y="' + (VH - 8) + '">LENGTH TO SCALE \u00b7 DIAMETERS PROPORTIONAL</text>');
      s.push('</svg>');
      el.sheet.innerHTML = s.join('');
    }

    function line(lbl, value, cls) {
      return '<div class="xms-pb__line' + (cls ? ' ' + cls : '') + '"><dt>' + esc(lbl) + '</dt><dd>' + esc(value) + '</dd></div>';
    }

    function endName(fl) { return fl ? fl.label : 'open'; }

    function render() {
      read();
      var p = price();
      draw(p);

      if (el.spec) {
        el.spec.textContent = p.t.label + ' \u00b7 ' + nn(p.t.od) + ' in OD \u00b7 A: ' + endName(p.fa) + ' \u00b7 B: ' + endName(p.fb);
      }
      if (el.weldcount) {
        el.weldcount.textContent = p.weldments + ' weldment' + (p.weldments === 1 ? '' : 's') + ' at ' + money(CFG.weldmentCents) + ' each';
      }

      var r = [];
      r.push(line('Weldments (' + p.weldments + ' \u00d7 ' + money(CFG.weldmentCents) + ')', money(p.weldCents)));
      if (p.lenValid) {
        r.push(line('Tube (' + nn(p.len) + ' in \u00d7 ' + money(p.t.rateCents) + '/in)', money(p.pipeCents)));
      } else {
        r.push(line('Tube', 'length out of range', 'is-warn'));
      }
      if (p.fa) r.push(line('End A \u2014 ' + p.fa.label, money(p.fa.cents)));
      if (p.fb) r.push(line('End B \u2014 ' + p.fb.label, money(p.fb.cents)));
      if (el.lines) el.lines.innerHTML = r.join('');

      var minTotal = Math.round(num(CFG.minTotalCents, 0));
      var sellable = CFG.armed === true && p.lenValid && p.total >= Math.max(1, minTotal);

      if (!p.lenValid) {
        el.total.textContent = '\u2014';
        el.charge.textContent = 'Enter a length between ' + MIN_L + ' and ' + MAX_L + ' in.';
      } else if (!sellable) {
        el.total.textContent = money(p.total);
        el.charge.textContent = CFG.armed ? 'Below our minimum for an online order \u2014 request a quote.' : 'Indicative only \u2014 pricing is not yet published.';
      } else {
        el.total.textContent = money(p.total);
        el.charge.textContent = 'Estimated \u2014 confirmed to the cent at checkout.';
      }

      if (el.cta) el.cta.disabled = !sellable;
      return { p: p, sellable: sellable };
    }

    var busy = false;

    /* The click handler no longer decides the price or picks a cart variant.
       It sends the configuration to the pricing Worker, which recomputes the
       total independently and creates a Draft Order at that exact price —
       the response is a checkout URL to redirect to, not a cart action. */
    function goToCheckout() {
      var st = render();
      if (!st.sellable || busy) return;
      if (!CFG.workerUrl) {
        if (el.status) el.status.textContent = 'Checkout is not configured. Contact us directly.';
        return;
      }
      busy = true;
      if (el.cta) { el.cta.disabled = true; el.cta.textContent = 'Preparing checkout\u2026'; }
      if (el.status) el.status.textContent = '';

      var p = st.p;
      var body = {
        tube: p.t.label,
        flangeA: p.fa ? p.fa.label : null,
        flangeB: p.fb ? p.fb.label : null,
        lengthIn: p.len
      };

      fetch(CFG.workerUrl.replace(/\/$/, '') + '/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
        .then(function (result) {
          if (result.ok && result.data.checkoutUrl) {
            window.location = result.data.checkoutUrl;
          } else {
            throw new Error(result.data && result.data.error ? result.data.error : 'Checkout failed.');
          }
        })
        .catch(function (e) {
          if (el.status) el.status.textContent = e.message || 'Could not start checkout. Please try again.';
        })
        .then(function () {
          busy = false;
          if (el.cta) { el.cta.disabled = false; el.cta.textContent = 'Continue to checkout'; }
        });
    }

    root.addEventListener('change', render);
    root.addEventListener('input', function (ev) { if (ev.target === el.len) render(); });
    if (el.cta) el.cta.addEventListener('click', goToCheckout);

    render();
  }

  function boot(root) {
    if (!root || root.getAttribute('data-pb-ready') === '1') return;
    root.setAttribute('data-pb-ready', '1');
    var sheet = root.querySelector('[data-pb="sheet"]');
    function fail(msg) {
      if (sheet) sheet.innerHTML = '<p class="xms-pb__empty">' + esc(msg) + '</p>';
    }
    try {
      init(root, sheet, fail);
    } catch (e) {
      fail('Builder error: ' + (e && e.message ? e.message : String(e)));
      if (window.console && console.error) console.error('[xms-pipe-builder]', e);
    }
  }

  function scan() {
    var list = document.querySelectorAll('[data-xms-pb]');
    for (var i = 0; i < list.length; i++) { boot(list[i]); }
  }

  window.XMS_PB_LOADED = true;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', scan);
})();
